import React from 'react';
import { Check, ArrowRight, Sparkles, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { CONTACT_ROUTE, DASHBOARD_SIGNIN_URL, DASHBOARD_SIGNUP_URL, SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';
import { ROUTES } from '@/config/routes';

const Pricing = () => {
  const platformFeatureGroups = [
    {
      title: 'Shipping modes',
      items: ['SDD', 'NDD', 'International', 'B2B']
    },
    {
      title: 'Operations & communication',
      items: [
        'WhatsApp communication',
        'Early COD',
        'NDR management',
        'RTO prediction',
        'Order confirmation',
        'Address confirmation'
      ]
    },
    {
      title: 'Team & support',
      items: ['User/employee management', 'Dedicated support']
    },
    {
      title: 'Commerce integrations',
      items: ['Shopify order sync', 'Amazon order sync', 'Order and inventory management']
    }
  ];

  const faqs = [
    {
      question: 'How do I start shipping with Boxd Logistics?',
      answer:
        `Sign up for a free account, add funds to your wallet, and start placing orders in minutes. Our team will reach out after your first recharge to walk you through the platform. For urgent queries, email ${SUPPORT_EMAIL} and we will get back to you quickly.`
    },
    {
      question: 'Is Boxd Logistics a courier partner?',
      answer:
        'We work as a logistics aggregator: we partner with leading Indian carriers so you get competitive rates, one dashboard, and simpler operations—not a single courier’s captive network.'
    },
    {
      question:
        'Does Boxd support logistics and order management for Shopify, Amazon, and similar channels?',
      answer:
        'Yes. You can connect marketplaces and stores such as Shopify, Amazon, and WooCommerce to manage shipments from one place. Inventory management is not included today—focus is on shipping and order fulfilment workflows.'
    },
    {
      question: 'Do you support B2B and B2C?',
      answer: 'Yes. Boxd supports both B2C and B2B shipping use cases.'
    }
  ];

  const plans = [
    {
      name: 'Standard',
      badge: 'Pay as you ship',
      price: '₹22',
      unit: 'starting per 500g',
      description: 'Access the full shipping platform with transparent, no lock-in pricing.',
      points: [
        'No setup fee or monthly subscription',
        'Multi-courier access from one dashboard',
        'Real-time tracking + automated labels',
        'Core NDR and COD operational controls'
      ],
      ctaLabel: 'Start now',
      ctaType: 'primary'
    },
    {
      name: 'Enterprise',
      badge: 'For high-volume businesses',
      price: 'Custom',
      unit: 'tailored commercial package',
      description: 'Design a custom package for large monthly volumes or unique shipping workflows.',
      points: [
        'Volume-based discounted slabs',
        'Dedicated account and technical support',
        'Custom integrations and workflow fitment',
        'Country/lane specific commercial structures'
      ],
      ctaLabel: 'Contact sales',
      ctaType: 'outline'
    }
  ];

  const serviceLevels = [
    {
      title: 'Basic Shipping',
      time: '5-7 business days',
      detail: 'Economical option for non-urgent deliveries.'
    },
    {
      title: 'Standard Shipping',
      time: '2-4 business days',
      detail: 'Balanced speed and cost for regular eCommerce orders.'
    },
    {
      title: 'Express Shipping',
      time: '1-2 business days',
      detail: 'Fastest option for urgent and high-priority shipments.'
    }
  ];

  const commercialNotes = [
    {
      title: 'Transparent weight management',
      detail: 'Weekly reconciliations and audit-friendly billing to reduce surprises.'
    },
    {
      title: 'COD flow support',
      detail: 'Early COD support with tracking visibility and faster operational clarity.'
    },
    {
      title: 'No setup lock-in',
      detail: 'No subscription lock-in at the entry level - pay as you ship.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Shipping Pricing for eCommerce Brands"
        description="Transparent shipping plans for startups to enterprises with dynamic courier allocation, NDR management, RTO prediction, and cross-border support."
        keywords="ecommerce shipping pricing, courier pricing india, shipping plans, logistics pricing, boxd logistics pricing"
        canonical="https://boxdlogistics.in/pricing"
      />
      <Navbar />

      <section className="relative pt-28 pb-20 md:pt-32 md:pb-24 px-4 overflow-hidden bg-gradient-to-b from-blue-100/40 via-blue-50/60 to-white">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-blue-50/80 pointer-events-none" />
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[min(120vw,900px)] h-[480px] rounded-full pointer-events-none opacity-90"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.22) 0%, rgba(219, 234, 254, 0.35) 35%, transparent 70%)'
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.65]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.06) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              'linear-gradient(rgba(30, 64, 175, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(30, 64, 175, 0.04) 1px, transparent 1px)',
            backgroundSize: '96px 96px'
          }}
        />
        <div className="absolute -top-20 -right-20 w-80 h-80 md:w-[28rem] md:h-[28rem] rounded-full bg-gradient-to-br from-blue-300/30 to-blue-700/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-24 w-72 h-72 md:w-96 md:h-96 rounded-full bg-gradient-to-tr from-emerald-200/25 to-blue-200/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full bg-gradient-to-t from-blue-500/15 to-transparent blur-2xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/50 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent pointer-events-none" />

        <div className="relative max-w-6xl mx-auto z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/90 px-4 py-1.5 text-sm font-medium text-blue-900 shadow-sm mb-6">
                <Sparkles className="w-4 h-4" />
                Pricing &amp; plans
              </div>

              <p className="text-lg md:text-xl text-gray-600 max-w-2xl lg:max-w-xl mb-4 mx-auto lg:mx-0">
                Choose a pricing model built for growth with transparent logistics commercials and flexible service levels.
              </p>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900 mb-2">
                Pay less,{' '}
                <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
                  ship more
                </span>
              </h1>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-5 mb-8">
                Affordable pricing
              </h2>

              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-10 py-7 text-lg rounded-xl shadow-lg shadow-blue-800/25"
                onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}
              >
                Start now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <div className="mt-5 flex flex-wrap justify-center lg:justify-start gap-2">
                <span className="inline-flex items-center rounded-full border border-blue-200 bg-white/90 px-3 py-1 text-xs font-semibold text-blue-900">
                  We are on Shopify
                </span>
                <span className="inline-flex items-center rounded-full border border-blue-200 bg-white/90 px-3 py-1 text-xs font-semibold text-blue-900">
                  Made with love in India
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl border-2 border-blue-200 bg-white p-8 md:p-10 shadow-xl shadow-blue-700/10">
                <p className="text-gray-700 text-sm font-semibold uppercase tracking-wide">Transparent commercials</p>
                <p className="text-gray-600 text-base mt-3 mb-7">Free sign-up · shipping starts at</p>
                <div className="flex items-end gap-3">
                  <span className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent leading-none">
                    ₹22
                  </span>
                  <span className="text-lg font-semibold text-gray-700 pb-2">per 500g</span>
                </div>
                <div className="mt-7 pt-6 border-t border-gray-200/80">
                  <p className="text-sm text-gray-600">
                    No setup fee • No lock-in • Built for high-growth D2C and marketplace brands • T&C applied.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Plan Snapshot</h3>
            <p className="text-gray-600 mt-2">Simple standard pricing for most businesses, and custom commercials for advanced needs.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl border p-7 shadow-sm transition-all hover:shadow-lg h-full flex flex-col ${
                  plan.name === 'Standard' ? 'border-blue-300 bg-gradient-to-br from-white to-blue-50/40' : 'border-gray-200 bg-white'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-2">{plan.badge}</p>
                <h4 className="text-2xl font-bold text-gray-900">{plan.name}</h4>
                <div className="mt-4">
                  <p className="text-3xl font-extrabold text-gray-900">{plan.price}</p>
                  <p className="text-sm text-gray-600">{plan.unit}</p>
                </div>
                <p className="text-sm text-gray-600 mt-4">{plan.description}</p>
                <ul className="mt-5 space-y-3 flex-1">
                  {plan.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 mt-0.5 text-blue-800" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  {plan.ctaType === 'primary' ? (
                    <Button
                      className="w-full bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white"
                      onClick={() => { window.location.href = DASHBOARD_SIGNIN_URL; }}
                    >
                      {plan.ctaLabel} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-2 border-blue-700 text-blue-900 hover:bg-blue-50"
                      onClick={() => { window.location.href = CONTACT_ROUTE; }}
                    >
                      {plan.ctaLabel}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            *<Link to={ROUTES.TERMS} className="underline hover:text-blue-800">Terms &amp; conditions</Link> apply.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Platform features built for growth</h3>
            <p className="text-gray-600 mt-3 max-w-3xl mx-auto">
              Ship faster, improve delivery outcomes, and manage operations across channels from one dashboard.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {platformFeatureGroups.map((group) => (
              <div
                key={group.title}
                className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/40 p-7 md:p-8 shadow-sm"
              >
                <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-5">{group.title}</h4>
                <ul className="space-y-3">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-700 text-white">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      <span className="text-gray-700 leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>
      </section>

      <section className="py-14 px-4 bg-gray-50/70 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {serviceLevels.map((level) => (
              <div key={level.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-blue-800 font-semibold">{level.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{level.time}</p>
                <p className="text-gray-600 mt-2 text-sm">{level.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8">
            Commercial clarity that scales with you
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {commercialNotes.map((note) => (
              <div key={note.title} className="rounded-2xl border border-blue-200/70 bg-blue-50/40 p-6">
                <p className="font-semibold text-gray-900">{note.title}</p>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{note.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Come ship with us</h2>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            First-mile pickup to last-mile delivery—one partner for growing eCommerce brands across India.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-blue-800 hover:bg-blue-700 text-white px-8 py-6 rounded-xl"
              onClick={() => { window.location.href = DASHBOARD_SIGNUP_URL; }}
            >
              Get started
            </Button>
            <a
              href={SUPPORT_EMAIL_MAILTO}
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-300 hover:text-blue-200 transition-colors"
            >
              <Mail className="w-4 h-4" />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-20 px-4 bg-gray-50/80">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-blue-800 font-semibold text-sm uppercase tracking-wider mb-2">Help</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              FAQs
            </h2>
            <p className="text-gray-600 mt-2">Frequently asked questions</p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-100 rounded-xl px-4 data-[state=open]:bg-blue-50/30 transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-800 py-4 text-[15px] md:text-base">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm md:text-base pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
